import type { ReactNode } from 'react';
import { Check } from 'lucide-react';
import * as S from './styles';

export type RouteStep = 'center' | 'warehouses' | 'review';

export const ROUTE_STEPS: { id: RouteStep; title: string }[] = [
  { id: 'center', title: 'Centro no mapa' },
  { id: 'warehouses', title: 'Armazéns' },
  { id: 'review', title: 'Revisar e ativar' },
];

interface RouteStepsProps {
  current: RouteStep;
  done: Record<RouteStep, boolean>;
  onSelect: (step: RouteStep) => void;
}

// Os passos para montar um rastreio, em ordem. Quem já conhece pode pular para qualquer um.
export const RouteSteps: React.FC<RouteStepsProps> = ({
  current,
  done,
  onSelect,
}) => (
  <nav aria-label="Passos do rastreio">
    <S.StepList>
      {ROUTE_STEPS.map(({ id, title }, index) => (
        <li key={id} className="flex min-w-0 flex-1 items-center gap-2">
          {index > 0 ? (
            <S.StepConnector
              aria-hidden="true"
              $done={done[ROUTE_STEPS[index - 1].id]}
            />
          ) : null}
          <S.StepButton
            type="button"
            aria-current={id === current ? 'step' : undefined}
            $current={id === current}
            onClick={() => onSelect(id)}
          >
            <S.StepNumber $current={id === current} $done={done[id]}>
              {done[id] ? (
                <Check size={14} aria-label="concluído" />
              ) : (
                index + 1
              )}
            </S.StepNumber>
            <span className="truncate">{title}</span>
          </S.StepButton>
        </li>
      ))}
    </S.StepList>
  </nav>
);

interface StepPanelProps {
  step: RouteStep;
  children: ReactNode;
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
}

// Conteúdo de um passo, com Voltar e Próximo no rodapé.
export const StepPanel: React.FC<StepPanelProps> = ({
  step,
  children,
  onBack,
  onNext,
  nextLabel,
}) => {
  const index = ROUTE_STEPS.findIndex(({ id }) => id === step);
  return (
    <S.Panel aria-label={`Passo ${index + 1}: ${ROUTE_STEPS[index].title}`}>
      <S.PanelHeader>
        <h2 className="font-bold text-slate-800">{`${index + 1}. ${ROUTE_STEPS[index].title}`}</h2>
      </S.PanelHeader>
      <div className="flex flex-col gap-4 p-4">{children}</div>
      {onBack || onNext ? (
        <footer className="flex justify-between gap-2 border-t border-slate-200 px-4 py-3">
          {onBack ? (
            <S.SecondaryButton type="button" onClick={onBack}>
              Voltar
            </S.SecondaryButton>
          ) : (
            <span />
          )}
          {onNext ? (
            <S.PrimaryButton type="button" onClick={onNext}>
              {nextLabel ?? 'Próximo'}
            </S.PrimaryButton>
          ) : null}
        </footer>
      ) : null}
    </S.Panel>
  );
};
